import { useCallback, useEffect, useRef, useState } from 'react';
import { Field, Note, Panel, Spinner } from '@wyocspay/ui';
import { ApiError, api, type OnboardingDoc, type OnboardingState } from './api.js';

/**
 * The identity-verification / KYC gate, from the merchant's side. Stripe has
 * verified who they are, but does not hand back the company registration number,
 * the tax number or the representative's role - so the merchant enters those,
 * backs them with a document, and submits for the platform's own review. Which of
 * the four steps is shown is decided entirely by the server's `step`.
 */

const KIND_LABELS: Record<string, string> = {
  ein_confirmation: 'EIN confirmation letter',
  incorporation: 'Certificate / contract of incorporation',
  tax_registration: 'Tax registration',
  vat_registration: 'VAT registration',
  other: 'Other supporting document',
};

export function VerificationView({ onGoToDocuments }: { onGoToDocuments: () => void }) {
  const [state, setState] = useState<OnboardingState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [changing, setChanging] = useState(false);

  const load = useCallback(async () => {
    try {
      setState(await api.onboarding());
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load your verification status.');
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  if (error && !state) return <Note tone="danger">{error}</Note>;
  if (!state) return <Spinner label="Loading…" />;

  if (state.step === 'review') {
    return (
      <Note tone="info" title="Your details are being reviewed">
        Thanks - your details and documents have been submitted. Our team is reviewing them and you will be
        able to continue as soon as they are approved. You will see the result here.
      </Note>
    );
  }

  if (state.step === 'signing') {
    return (
      <Panel title="One last step - sign your agreement">
        <div style={{ display: 'grid', gap: 12 }}>
          <Note tone="ok">Your details have been approved.</Note>
          <p style={{ margin: 0 }}>
            Before you can use the platform, please review and sign the required document
            {state.mandatory.length > 1 ? 's' : ''}:
          </p>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {state.mandatory.map((m) => (
              <li key={m.key} style={{ marginBottom: 4 }}>
                {m.title} {m.signed ? '- signed ✓' : '- awaiting your signature'}
              </li>
            ))}
          </ul>
          <div>
            <button type="button" className="wc-btn" onClick={onGoToDocuments}>
              Go to documents to sign
            </button>
          </div>
        </div>
      </Panel>
    );
  }

  if (state.step === 'open') {
    if (changing) {
      return <DataStep state={state} reload={load} changeMode onSubmitted={() => setChanging(false)} />;
    }
    return (
      <div style={{ display: 'grid', gap: 12 }}>
        <Note tone="ok" title="You're all set">
          Your verification is complete and your account is fully active.
        </Note>
        {state.verified ? (
          <div>
            <button type="button" className="wc-btn wc-btn-secondary" onClick={() => setChanging(true)}>
              Request a change to your business details
            </button>
            <p style={{ margin: '8px 0 0', fontSize: 12.5, color: 'var(--wc-faint)' }}>
              Update your company number, tax details or representative and attach proof; you stay active
              while we review the change.
            </p>
          </div>
        ) : null}
      </div>
    );
  }

  return <DataStep state={state} reload={load} />;
}

function DataStep({
  state,
  reload,
  changeMode,
  onSubmitted,
}: {
  state: OnboardingState;
  reload: () => Promise<void>;
  changeMode?: boolean;
  onSubmitted?: () => void;
}) {
  const [data, setData] = useState(state.data);
  const [docs, setDocs] = useState<OnboardingDoc[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploadKind, setUploadKind] = useState('ein_confirmation');

  const loadDocs = useCallback(async () => {
    try {
      setDocs((await api.onboardingDocuments()).documents);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  }, []);
  useEffect(() => {
    void loadDocs();
  }, [loadDocs]);

  const set = (k: keyof typeof data, v: string) => {
    setData({ ...data, [k]: v });
    setSavedFlash(false);
  };

  const saveData = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.saveOnboardingData(data);
      setSavedFlash(true);
      await reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const toBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const s = String(reader.result);
        resolve(s.slice(s.indexOf(',') + 1));
      };
      reader.onerror = () => reject(new Error('Could not read the file.'));
      reader.readAsDataURL(file);
    });

  const upload = async (file: File) => {
    setBusy(true);
    setError(null);
    try {
      if (file.size > 15_000_000) throw new Error('The file is too large (max 15 MB).');
      const base64 = await toBase64(file);
      await api.uploadOnboardingDocument({
        kind: uploadKind,
        filename: file.name,
        mime_type: file.type || 'application/octet-stream',
        data: base64,
      });
      await loadDocs();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const removeDoc = async (id: string) => {
    setBusy(true);
    try {
      await api.deleteOnboardingDocument(id);
      await loadDocs();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.saveOnboardingData(data);
      await api.submitOnboarding();
      onSubmitted?.();
      await reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const input: React.CSSProperties = {
    width: '100%',
    padding: '9px 11px',
    border: '1px solid var(--wc-border)',
    borderRadius: 8,
    fontSize: 14,
    background: 'var(--wc-bg, #fff)',
    color: 'var(--wc-ink)',
  };

  const canSubmit = state.missing.length === 0 && (docs?.length ?? 0) > 0;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Note title={changeMode ? 'Request a change to your business details' : 'Verify your business details to activate your account'}>
        {changeMode
          ? 'Update the details below and attach a document that proves the change. Your account stays active while our team reviews it; the change takes effect once it is approved.'
          : 'Stripe has verified your identity, but we need a few details it does not share back so your legal documents and account are complete. Enter them, attach a document that proves them, and submit - our team approves it and you are ready to go.'}
      </Note>

      {state.status === 'REJECTED' && state.rejectionReason ? (
        <Note tone="danger" title="Please correct and resubmit">
          {state.rejectionReason}
        </Note>
      ) : null}

      <Panel title="Business details">
        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
          <Field label="Company registration number *">
            <input style={input} value={data.company_registration_no} maxLength={64} onChange={(e) => set('company_registration_no', e.target.value)} />
          </Field>
          <Field label="EIN / Tax ID">
            <input style={input} value={data.tax_id} maxLength={64} placeholder="e.g. 88-1234567" onChange={(e) => set('tax_id', e.target.value)} />
          </Field>
          <Field label="VAT number">
            <input style={input} value={data.vat} maxLength={64} onChange={(e) => set('vat', e.target.value)} />
          </Field>
          <Field label="DBA / Trading name">
            <input style={input} value={data.dba} maxLength={120} onChange={(e) => set('dba', e.target.value)} />
          </Field>
          <Field label="Representative title *">
            <input style={input} value={data.representative_title} maxLength={80} placeholder="e.g. Owner, Director" onChange={(e) => set('representative_title', e.target.value)} />
          </Field>
          <Field label="Representative role">
            <input style={input} value={data.representative_role} maxLength={80} placeholder="e.g. Managing Member" onChange={(e) => set('representative_role', e.target.value)} />
          </Field>
        </div>
        <p style={{ margin: '8px 0 0', fontSize: 12.5, color: 'var(--wc-faint)' }}>
          Provide your company registration number, at least one of EIN or VAT, and your title. (*)
        </p>
        {savedFlash ? <div style={{ marginTop: 8 }}><Note tone="ok">Saved.</Note></div> : null}
        <div style={{ marginTop: 12 }}>
          <button type="button" className="wc-btn wc-btn-secondary" onClick={saveData} disabled={busy}>
            {busy ? 'Saving…' : 'Save details'}
          </button>
        </div>
      </Panel>

      <Panel title="Proof documents">
        <Note>
          Attach a document that proves your details - an EIN confirmation letter, a certificate or contract
          of incorporation (any country), a tax or VAT registration, or similar. PDF, JPG or PNG, up to 15 MB.
        </Note>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', margin: '10px 0' }}>
          <select className="wc-input" style={{ width: 'auto' }} value={uploadKind} onChange={(e) => setUploadKind(e.target.value)}>
            {Object.entries(KIND_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <label className="wc-btn wc-btn-secondary wc-btn-sm" style={{ cursor: 'pointer', margin: 0 }}>
            {busy ? 'Uploading…' : 'Attach file'}
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf,image/png,image/jpeg,image/webp"
              style={{ display: 'none' }}
              disabled={busy}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void upload(f);
                e.currentTarget.value = '';
              }}
            />
          </label>
        </div>
        {docs === null ? (
          <Spinner label="Loading documents…" />
        ) : docs.length === 0 ? (
          <p style={{ margin: 0, fontSize: 13, color: 'var(--wc-faint)' }}>No documents attached yet.</p>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {docs.map((d) => (
              <div key={d.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '8px 12px', border: '1px solid var(--wc-border)', borderRadius: 8 }}>
                <span style={{ minWidth: 0 }}>
                  <span style={{ fontWeight: 600, fontSize: 13 }}>{d.filename}</span>
                  <span style={{ fontSize: 12, color: 'var(--wc-muted)', marginLeft: 8 }}>
                    {KIND_LABELS[d.kind] ?? d.kind} · {(d.size / 1024).toFixed(0)} KB
                  </span>
                </span>
                <button type="button" className="wc-btn wc-btn-secondary wc-btn-sm" onClick={() => removeDoc(d.id)} disabled={busy}>
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </Panel>

      {error ? <Note tone="danger">{error}</Note> : null}

      <Panel title={changeMode ? 'Submit change for review' : 'Submit for review'}>
        <p style={{ margin: '0 0 12px', fontSize: 14 }}>
          {changeMode
            ? 'Submit the updated details and proof for approval. You stay active while we review; the change applies once approved.'
            : 'Once your details are entered and a proof document is attached, submit them for approval. You will be notified here when the review is complete.'}
        </p>
        <button type="button" className="wc-btn" onClick={submit} disabled={busy || !canSubmit}>
          {changeMode ? 'Submit change for review' : 'Submit for review'}
        </button>
        {!canSubmit ? (
          <p style={{ margin: '8px 0 0', fontSize: 12.5, color: 'var(--wc-faint)' }}>
            Complete the required details (*) and attach at least one document to enable submission.
          </p>
        ) : null}
      </Panel>
    </div>
  );
}
